import { useState, useEffect, useCallback } from "react";
import { Wrench, ShieldCheck, Clock } from "lucide-react";
import { API, useAuth } from "../App";

/**
 * Polls /api/system/maintenance/public every 60s.
 * - When maintenance is ACTIVE and user is NOT admin: full-screen blocking overlay.
 * - When maintenance is ACTIVE and user IS admin: a thin top banner (admin can still use app).
 * - When maintenance is SCHEDULED ≤24h away: a thin top banner for everyone.
 * - Otherwise: renders nothing.
 */

const POLL_MS = 60_000;

function fmtCountdown(seconds) {
  if (seconds == null || seconds <= 0) return "";
  if (seconds < 60) return `in ${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `in ${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm ? `in ${h}h ${mm}m` : `in ${h} hour${h === 1 ? "" : "s"}`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `in ${d}d ${hh}h` : `in ${d} day${d === 1 ? "" : "s"}`;
}

function fmtLocal(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

export default function MaintenanceOverlay() {
  const { user } = useAuth();
  const [maint, setMaint] = useState(null);
  const [tick, setTick] = useState(0);
  const isAdmin = user?.role === "admin";

  const fetchMaint = useCallback(async () => {
    try {
      const res = await fetch(`${API}/system/maintenance/public`);
      if (res.ok) setMaint(await res.json());
    } catch {
      // Silent: if the network is down, the app is broken anyway.
    }
  }, []);

  useEffect(() => {
    fetchMaint();
    const id = setInterval(fetchMaint, POLL_MS);
    return () => clearInterval(id);
  }, [fetchMaint]);

  // Local 1s tick to refresh countdown text smoothly (when ≤ 5 minutes away)
  useEffect(() => {
    if (!maint) return;
    const seconds = maint.starts_in_seconds ?? maint.ends_in_seconds;
    if (seconds == null || seconds > 300) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [maint]);

  if (!maint) return null;

  const upcoming = !maint.is_active && maint.scheduled_start && maint.starts_in_seconds != null && maint.starts_in_seconds <= 86400;

  // ── ACTIVE MAINTENANCE ────────────────────────────────────────────
  if (maint.is_active) {
    if (isAdmin) {
      // Admin bypass — show top banner only
      return (
        <div data-testid="maintenance-admin-banner" className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-cosmic-void px-4 py-2 text-sm font-medium flex items-center gap-3 shadow-lg">
          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
          <span className="font-semibold">ADMIN BYPASS:</span>
          <span className="truncate">Maintenance is active for end-users — {maint.message || "system update in progress"}</span>
          {maint.ends_in_seconds != null && <span className="ml-auto whitespace-nowrap opacity-80">ends {fmtCountdown(maint.ends_in_seconds)}</span>}
        </div>
      );
    }

    // Non-admin → full-screen block
    return (
      <div data-testid="maintenance-overlay" className="fixed inset-0 z-[100] bg-cosmic-void/95 backdrop-blur-md flex items-center justify-center p-6" key={tick}>
        <div className="max-w-lg w-full bg-cosmic-void border border-divine-gold/40 rounded-2xl p-8 shadow-2xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-divine-gold/10 mb-6">
            <Wrench className="w-8 h-8 text-divine-gold" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading text-divine-gold mb-3">System Under Maintenance</h1>
          <p className="text-chakra-crown/80 text-base mb-6 leading-relaxed">
            {maint.message || "We are currently updating the system. Please check back shortly."}
          </p>
          {maint.ends_in_seconds != null && (
            <div className="flex items-center justify-center gap-2 text-sm text-chakra-crown/60 mb-2" data-testid="maintenance-countdown">
              <Clock className="w-4 h-4" />
              <span>Back online <span className="text-divine-gold font-medium">{fmtCountdown(maint.ends_in_seconds)}</span></span>
            </div>
          )}
          {maint.scheduled_end && (
            <div className="text-xs text-chakra-crown/40">
              ETA: {fmtLocal(maint.scheduled_end)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── UPCOMING MAINTENANCE (≤24h) ───────────────────────────────────
  if (upcoming) {
    return (
      <div data-testid="maintenance-upcoming-banner" className="fixed top-0 inset-x-0 z-50 bg-amber-500/90 text-cosmic-void px-4 py-2 text-sm font-medium flex items-center gap-3 shadow-lg">
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span className="font-semibold">Scheduled maintenance {fmtCountdown(maint.starts_in_seconds)}:</span>
        <span className="truncate">{maint.message || "We will be briefly offline for an update."}</span>
        <span className="ml-auto whitespace-nowrap opacity-80 hidden sm:inline">{fmtLocal(maint.scheduled_start)}</span>
      </div>
    );
  }

  return null;
}
